import type { CapturedUrlImage, CapturedUrlSite } from "./url-import-adapter";

export type RemoteAssetCandidate = {
  id: string;
  url: string;
  altText: string;
  width?: number;
  height?: number;
  sourcePages: string[];
};

function normalizeRemoteAssetUrl(
  rawUrl: string,
  sourceUrl: string,
): string | null {
  const value = String(rawUrl || "").trim();

  if (!value) return null;

  try {
    const url = new URL(value, sourceUrl);

    if (!["http:", "https:"].includes(url.protocol)) {
      return null;
    }

    // A page captured securely should not retain an HTTP reference to the
    // same host when the equivalent HTTPS asset URL is available.
    const source = new URL(sourceUrl);

    if (
      source.protocol === "https:" &&
      url.protocol === "http:" &&
      url.hostname.toLowerCase() === source.hostname.toLowerCase()
    ) {
      url.protocol = "https:";
    }

    url.hash = "";

    return url.toString();
  } catch {
    return null;
  }
}

function createRemoteAssetCandidateId(url: string): string {
  let hash = 2166136261;

  for (let index = 0; index < url.length; index += 1) {
    hash ^= url.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return `remote-asset-${(hash >>> 0).toString(36)}`;
}

function candidateAltText(image: CapturedUrlImage): string {
  return String(image.alt || "")
    .replace(/\s+/g, " ")
    .trim();
}

export function collectRemoteAssetCandidates(
  capture: CapturedUrlSite,
): RemoteAssetCandidate[] {
  const candidates = new Map<string, RemoteAssetCandidate>();

  for (const page of capture.pages || []) {
    for (const image of page.images || []) {
      const url = normalizeRemoteAssetUrl(image.src, page.sourceUrl);

      if (!url) continue;

      const existing = candidates.get(url);

      if (existing) {
        if (!existing.sourcePages.includes(page.sourceUrl)) {
          existing.sourcePages.push(page.sourceUrl);
        }

        if (!existing.altText) {
          existing.altText = candidateAltText(image);
        }

        existing.width = Math.max(
          existing.width || 0,
          Number(image.width) || 0,
        );

        existing.height = Math.max(
          existing.height || 0,
          Number(image.height) || 0,
        );

        continue;
      }

      candidates.set(url, {
        id: createRemoteAssetCandidateId(url),
        url,
        altText: candidateAltText(image),
        width: Number(image.width) || undefined,
        height: Number(image.height) || undefined,
        sourcePages: [page.sourceUrl],
      });
    }
  }

  return [...candidates.values()].sort((left, right) =>
    left.url.localeCompare(right.url),
  );
}
